// src/pages/schedule-timing.js
import React, { useState, useEffect, useContext } from "react";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { request } from "../utils/request";
import { ColorModeContext } from "../index"; // ← import your theme context

const SchedulerDialogComponent = () => {
  // consume theme mode
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === "dark";
  const textColor = isDark ? "#FFFFFF" : "#000000";

  // State for dialog visibility and form fields
  const [dialogOpen, setDialogOpen] = useState(false);
  const [frequencyType, setFrequencyType] = useState("");
  const [time, setTime] = useState("");
  const [dayOfWeek, setDayOfWeek] = useState("");
  const [serviceName, setServiceName] = useState("");
  const [services, setServices] = useState([]);
  const [message, setMessage] = useState("");
  const [schedulers, setSchedulers] = useState([]);

  // Edit state
  const [isEdit, setIsEdit] = useState(false);
  const [editID, setEditID] = useState(null);

  // Fetch patchlist data and filter duplicates by serviceName
  const fetchData = async () => {
    try {
      const data = await request("/firmwarelist", { method: "GET" });
      if (data) {
        const uniqueServices = data.data.filter((item, index, self) =>
          index === self.findIndex((t) => t.serviceName === item.serviceName)
        );
        setServices(uniqueServices);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  // Fetch scheduler entries from API
  const fetchSchedulers = async () => {
    try {
      const data = await request("/scheduler", { method: "GET" });
      if (data.success) {
        setSchedulers(data.schedulers);
      }
    } catch (error) {
      console.error("Error fetching schedulers:", error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchSchedulers();
  }, []);

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setMessage("");
    // Reset form fields and edit state
    setFrequencyType("");
    setTime("");
    setDayOfWeek("");
    setServiceName("");
    setIsEdit(false);
    setEditID(null);
  };

  const handleClickOpenEdit = (sch) => {
    setIsEdit(true);
    setEditID(sch._id);
    setFrequencyType(sch.frequencyType);
    setTime(sch.time);
    setDayOfWeek(sch.frequencyType === "weekly" ? sch.dayOfWeek : "");
    setServiceName(sch.serviceName);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      frequencyType,
      time,
      dayOfWeek: frequencyType === "weekly" ? dayOfWeek : "",
      serviceName,
    };

    try {
      let data;
      if (isEdit && editID) {
        data = await request(`/scheduler/${editID}`, {
          method: "PUT",
          body: payload,
        });
      } else {
        data = await request("/scheduler", {
          method: "POST",
          body: payload,
        });
      }

      if (data.success) {
        setMessage("Scheduler saved successfully!");
        fetchSchedulers(); // Refresh table
        setTimeout(() => {
          handleCloseDialog();
        }, 1500);
      } else {
        setMessage("Failed to save scheduler.");
      }
    } catch (error) {
      console.error("Error saving scheduler:", error);
      setMessage("Error saving scheduler.");
    }
  };

  // Inline styles for modal dialog and table
  const modalOverlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: dialogOpen ? "flex" : "none",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  };

  const modalContentStyle = {
    backgroundColor: isDark ? "#2d3448" : "white",
    padding: "20px",
    borderRadius: "8px",
    width: "90%",
    maxWidth: "500px",
    boxSizing: "border-box",
    color: textColor,
  };

  const formFieldStyle = { marginBottom: "15px" };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: "20px",
    color: textColor,
  };

  const thStyle = {
    border: "1px solid #ddd",
    padding: "8px",
    backgroundColor: isDark ? "#2d3448" : "#f2f2f2",
    textAlign: "left",
    color: textColor,
  };

  const tdStyle = {
    border: "1px solid #ddd",
    padding: "8px",
    textAlign: "left",
    color: textColor,
  };

  // Filter out services that are already scheduled.
  // When editing, include the service being edited.
  const filteredServices = services.filter((service) => {
    if (isEdit && service.serviceName === serviceName) return true;
    return !schedulers.some((sch) => sch.serviceName === service.serviceName);
  });

  return (
    <div style={{ padding: "20px", fontFamily: "Arial, sans-serif", color: textColor }}>
      <h2 style={{ textAlign: "center", color: textColor }}>Scheduler Setup</h2>
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <button
          onClick={handleOpenDialog}
          style={{
            padding: "10px 20px",
            backgroundColor: "#1976d2",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        >
          Open Scheduler Form
        </button>
      </div>

      {/* Modal Dialog */}
      <div style={modalOverlayStyle}>
        <div style={modalContentStyle}>
          <h3 style={{ textAlign: "center", color: textColor }}>
            {isEdit ? "Edit Scheduler" : "Add Scheduler"}
          </h3>
          <form onSubmit={handleSubmit}>
            <div style={formFieldStyle}>
              <label style={{ fontWeight: "bold" }}>Frequency: </label>
              <select
                value={frequencyType}
                onChange={(e) => setFrequencyType(e.target.value)}
                style={{ width: "100%", padding: "8px", marginTop: "5px" }}
                required
              >
                <option value="">Select Frequency</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
            {frequencyType === "weekly" && (
              <div style={formFieldStyle}>
                <label style={{ fontWeight: "bold" }}>Day of the Week: </label>
                <select
                  value={dayOfWeek}
                  onChange={(e) => setDayOfWeek(e.target.value)}
                  style={{ width: "100%", padding: "8px", marginTop: "5px" }}
                  required
                >
                  <option value="">Select a day</option>
                  <option value="Monday">Monday</option>
                  <option value="Tuesday">Tuesday</option>
                  <option value="Wednesday">Wednesday</option>
                  <option value="Thursday">Thursday</option>
                  <option value="Friday">Friday</option>
                  <option value="Saturday">Saturday</option>
                  <option value="Sunday">Sunday</option>
                </select>
              </div>
            )}
            {frequencyType && (
              <div style={formFieldStyle}>
                <label style={{ fontWeight: "bold" }}>Time: </label>
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  style={{ width: "100%", padding: "8px", marginTop: "5px" }}
                  required
                />
              </div>
            )}
            <div style={formFieldStyle}>
              <label style={{ fontWeight: "bold" }}>Service: </label>
              <select
                value={serviceName}
                onChange={(e) => setServiceName(e.target.value)}
                style={{ width: "100%", padding: "8px", marginTop: "5px" }}
                required
              >
                <option value="">Select Service</option>
                {filteredServices.map((s) => (
                  <option key={s._id} value={s.serviceName}>
                    {s.serviceName}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ textAlign: "center", marginTop: "20px" }}>
              <button
                type="submit"
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#1976d2",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  marginRight: "10px",
                }}
              >
                {isEdit ? "Update Scheduler" : "Save Scheduler"}
              </button>
              <button
                type="button"
                onClick={handleCloseDialog}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#999",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </form>
          {message && (
            <p style={{ textAlign: "center", marginTop: "15px", color: textColor }}>
              {message}
            </p>
          )}
        </div>
      </div>

      {/* Scheduler Table */}
      <h3 style={{ textAlign: "center", marginTop: "30px", color: textColor }}>
        Current Scheduler Settings
      </h3>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Frequency</th>
            <th style={thStyle}>Day</th>
            <th style={thStyle}>Time</th>
            <th style={thStyle}>Service</th>
            <th style={thStyle}>Created At</th>
            <th style={thStyle}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {schedulers.map((sch) => (
            <tr
              key={sch._id}
              style={{ backgroundColor: isDark ? "#2d3448" : undefined }}
            >
              <td style={tdStyle}>{sch.frequencyType}</td>
              <td style={tdStyle}>
                {sch.frequencyType === "weekly" ? sch.dayOfWeek : "-"}
              </td>
              <td style={tdStyle}>{sch.time}</td>
              <td style={tdStyle}>{sch.serviceName}</td>
              <td style={tdStyle}>
                {new Date(sch.createdAt).toLocaleString()}
              </td>
              <td style={tdStyle}>
                <ModeEditIcon
                  style={{ cursor: "pointer", color: textColor }}
                  onClick={() => handleClickOpenEdit(sch)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SchedulerDialogComponent;
